import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useStore } from "modules/bridge/hyperlane/features/store";
import { BigNumber, ethers } from "ethers";
import { Deposit } from "modules/bridge/api/useBridges";
import { DESTINATION_NETWORK_BY_VALUE } from "modules/bridge/config";
import { HistoryHyperLineItem } from "modules/bridge/HistoryHyperLineItem";
import { HistoryItem } from "modules/bridge/HistoryItem";
import { ChangeEvent, useState } from "react";

export const decodeMetadata = (metadata: string): string[] | null => {
  if (!metadata || metadata === "0x") return ["", ""];
  try {
    const abiCoder = new ethers.utils.AbiCoder();
    return (
      abiCoder
        .decode(["string", "string"], metadata)
        .map((item) => item.trim()) || ["", ""]
    );
  } catch (error) {
    console.error("Error decoding metadata:", error);
    return ["", ""];
  }
};

const formatAmount = (amount: string) =>
  BigNumber.from(amount).div(BigNumber.from("1000000000000000000")).toString();

type SourceChainType = keyof typeof DESTINATION_NETWORK_BY_VALUE;

interface Props {
  list?: Deposit[];
  isLoading: boolean;
}

export const History = ({ list = [], isLoading }: Props) => {
  const { transfers: hyperlaneTransferList } = useStore((s) => s);

  const [selectedValue, setSelectedValue] = useState("ethereum");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  if (isLoading) {
    return <CircularProgress sx={{ display: "block", margin: "20px auto" }} />;
  }

  return (
    <>
      <Stack width="100%">
        <Typography variant="highlight">Choose network</Typography>
        <RadioGroup row value={selectedValue} onChange={handleChange}>
          {[
            {
              value: "ethereum",
              name: "Ethereum",
            },
            {
              value: "BSC",
              name: "BSC",
            },
          ].map(({ value, name }) => {
            return (
              <FormControlLabel
                disabled
                key={value}
                value={value}
                control={<Radio />}
                label={<Typography variant="body1">{name}</Typography>}
                sx={{
                  padding: "0 8px",
                  borderRadius: 2,
                }}
              />
            );
          })}
        </RadioGroup>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          padding: 3,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Table
          sx={{
            minWidth: "550px",
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                padding: 0,
                paddingBottom: 3,
              }}
            >
              {["Type", "Source", "Destination", ""].map((value) => {
                return (
                  <TableCell
                    key={value}
                    sx={{
                      padding: 0,
                      paddingBottom: 3,
                    }}
                  >
                    <Typography variant="body1">{value}</Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {hyperlaneTransferList.map(
              ({
                status,
                origin,
                destination,
                originTokenAddressOrDenom,
                destTokenAddressOrDenom,
                amount,
                // sender,
                recipient,
                originTxHash,
                // msgId,
                timestamp,
              }) => {
                return (
                  <HistoryHyperLineItem
                    status={status}
                    key={originTxHash}
                    timestamp={timestamp}
                    origin={origin}
                    originTokenAddressOrDenom={originTokenAddressOrDenom}
                    destTokenAddressOrDenom={destTokenAddressOrDenom}
                    recipient={recipient}
                    amount={amount}
                    destination={destination}
                    originTxHash={originTxHash}
                  />
                );
              }
            )}
            {list?.map((deposit) => {
              const [_, tokenSymbol] = decodeMetadata(deposit.metadata) || [
                "",
                "",
              ];

              const type = deposit.claim_tx_hash ? "Deposit" : "Withdraw";
              const amount = formatAmount(deposit.amount);
              const sourceChain =
                DESTINATION_NETWORK_BY_VALUE[
                  deposit.network_id as SourceChainType
                ];

              const destinationChain =
                DESTINATION_NETWORK_BY_VALUE[
                  deposit.dest_net as SourceChainType
                ];

              const historyProps = {
                txHash: deposit.tx_hash,
                type,
                blockNum: deposit.block_num,
                tokenSymbol,
                amount,
                sourceChainExplorer: sourceChain.explorer,
                sourceChainName: sourceChain.chain.name,
                destinationChainExplorer: destinationChain.explorer,
                destinationChainName: destinationChain.chain.name,
                depositReadyForClaim: deposit.ready_for_claim,
                depositClaimTxHash: deposit.claim_tx_hash,
                depositAmount: deposit.amount,
                depositCnt: deposit.deposit_cnt,
                destAddr: deposit.dest_addr,
                origNet: deposit.orig_net,
                destNet: deposit.dest_net,
                origAddr: deposit.orig_addr,
                globalIndex: deposit.global_index,
                claimTxHash: deposit.claim_tx_hash,
              };

              return <HistoryItem key={deposit.tx_hash} {...historyProps} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
